import { z } from 'zod';
import { DOCUMENT_SIGNATURE_PREFIX, digitalDocumentSchema, documentValuesSchema } from './structuredDocument.js';
import {
  AGENT_VOICES,
  CURRENCIES,
  CUSTOMER_MATCH_MODES,
  DISCOUNT_TYPES,
  INQUIRY_SOURCES,
  INQUIRY_STATUSES,
  INVOICE_DIRECTIONS,
  INVOICE_DOC_TYPES,
  LANDING_SECTION_KINDS,
  LIMITS,
  HISTORY_KINDS,
  QUOTE_TABS,
  REJECTION_REASONS,
  REMINDER_SOURCES,
  REMINDER_STATUSES,
  TIME_RANGES,
  CALL_STATUSES,
  TEMPLATE_KINDS,
  BUSINESS_ACCESS,
  PLATFORM_LEAD_STATUSES,
  SERVICE_AREAS,
} from './constants.js';
import { isLocale } from './i18n/index.js';
import type { SignatureAnchor } from './types.js';

/**
 * Request schemas. The API validates with these; the web app reuses them for
 * client-side form validation so both sides agree on the rules.
 *
 * Every `message` is an i18n KEY (see ./i18n), never literal text. The API
 * returns keys in `error.fields`; the client renders them with `t()`. This is
 * what keeps the product Hebrew-native without hardcoding Hebrew in logic.
 */

const trimmed = (max: number) => z.string().trim().max(max, 'validation.tooLong');

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'validation.email.required')
  .email('validation.email.invalid')
  .toLowerCase();

/**
 * AUTH-001 — what Worki needs before it opens an account.
 *
 * The four business details are required by both ways in: an email code proves
 * an address and Google proves an address, and neither says anything about the
 * business behind it. `acceptedTerms` is a literal `true` rather than a boolean
 * — a missing checkbox and an unticked one must both fail, and `z.boolean()`
 * would happily accept `false`.
 */
export const businessDetailsSchema = z.object({
  businessName: trimmed(120).min(2, 'validation.businessName.required'),
  name: trimmed(120).min(2, 'validation.name.required'),
  phone: trimmed(40).min(9, 'validation.phone.invalid'),
  tradeId: trimmed(40).min(1, 'validation.trade.required'),
  taxId: trimmed(20).regex(/^\d{8,9}$/, 'validation.taxId.invalid'),
  /**
   * Where the business is. Optional, unlike its four neighbours: it saves
   * typing on every later screen rather than identifying anyone, and a sign-up
   * form that refuses to go on over a convenience costs more than it saves.
   * `.default('')` so an older client that sends nothing reads the same as one
   * whose field was left blank.
   */
  city: trimmed(80).default(''),
  acceptedTerms: z.literal(true, {
    errorMap: () => ({ message: 'validation.terms.required' }),
  }),
});

export const signupSchema = businessDetailsSchema.extend({
  email: emailSchema,
});

/** The same details, handed in against a Google sign-in that proved the address. */
export const googleSignupSchema = businessDetailsSchema.extend({
  token: trimmed(120).min(10, 'validation.id.invalid'),
});

/**
 * Native Google Sign-In: the ID token JWT from the SDK, whose `aud` is the
 * web client (`serverClientId`). Not the authorization code the browser flow
 * exchanges — that path stays GET /google + /callback.
 */
export const googleNativeSchema = z.object({
  idToken: z.string().trim().min(1, 'errors.googleFailed'),
});

export const loginSchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/, 'validation.otp.invalid'),
});
export const requestLoginCodeSchema = z.object({ email: emailSchema });

/**
 * ACCT-DEL — deleting your own account.
 *
 * The address is typed again rather than taken from the session. Nothing here
 * is recoverable and the request carries no other argument, so without it a
 * mis-aimed tap is a deleted business; with it the person has to write out
 * which account they mean. `emailSchema` lowercases both sides, so the
 * comparison does not turn on capitals.
 */
export const deleteAccountSchema = z.object({ email: emailSchema });

/** Step 2 of sign-up: the pending row already holds the business and name. */
export const verifySignupSchema = z.object({
  email: emailSchema,
  code: z.string().regex(/^\d{6}$/, 'validation.otp.invalid'),
});

export const otpSchema = z.object({ code: z.string().regex(/^\d{6}$/, 'validation.otp.invalid') });
export const subscriptionSchema = z.object({ plan: z.enum(['starter','business','pro']) });

/** AUTH-009 — starts a hosted checkout for the chosen plan. */
export const checkoutSchema = z.object({ plan: z.enum(['starter', 'business', 'pro']) });

export const updateProfileSchema = z.object({
  name: trimmed(120).min(2, 'validation.name.required').optional(),
  businessName: trimmed(120).min(2, 'validation.businessName.required').optional(),
  businessEmail: z.union([emailSchema, z.literal('')]).optional(),
  phone: trimmed(40).optional(),
  address: trimmed(200).optional(),
  taxId: trimmed(40).optional(),
  currency: z.enum(CURRENCIES).optional(),
  slogan: trimmed(80).optional(),
  quoteFooter: trimmed(300).optional(),
  /*
   * VAT is not here any more. The rate is a national number Worki holds
   * centrally — one edit on the admin screen rather than a field every owner
   * has to find and keep right — and whether prices already include it is
   * decided on the quote being written, where the number it changes is on
   * screen. The columns stay on the business row as the historical record of
   * what an account was configured with; nothing writes them now.
   */
  /**
   * QUO-012 — the no-response follow-up threshold. Capped at 168 because the
   * interactive quote link is valid for 7 days: a threshold past that would
   * never fire before the quote expires and the follow-up becomes moot.
   */
  quoteReminderHours: z.coerce
    .number()
    .int()
    .min(1, 'validation.quoteReminderHours.invalid')
    .max(168, 'validation.quoteReminderHours.invalid')
    .optional(),
  /**
   * SIG-004 — the same threshold for a document nobody has signed. Capped at
   * 720 rather than 168 because the signing link is valid for 30 days: the cap
   * is the point past which the chase could never fire, and that point is
   * different for the two documents.
   */
  signatureReminderHours: z.coerce
    .number()
    .int()
    .min(1, 'validation.signatureReminderHours.invalid')
    .max(720, 'validation.signatureReminderHours.invalid')
    .optional(),
  /**
   * TEL-001 — the line the provider routes to this user. Provisioning it
   * automatically needs a Twilio account; typing in a number you already own
   * makes the inbound webhook and click-to-call work today.
   */
  virtualNumber: trimmed(40).optional(),
});

export const customerSchema = z.object({
  names: z.array(z.union([trimmed(120).min(2, 'validation.customerName.required'), z.literal('')])).max(20).transform((rows) => rows.filter(Boolean)).optional(),
  phones: z.array(trimmed(40)).max(20).transform((rows) => rows.filter(Boolean)).optional(),
  emails: z.array(z.union([emailSchema, z.literal('')])).max(20).transform((rows) => rows.filter(Boolean)).optional(),
  name: trimmed(120).min(2, 'validation.customerName.required'),
  phone: trimmed(40).optional(),
  email: z.union([emailSchema, z.literal('')]).optional(),
  city: trimmed(80).optional(),
});

/**
 * What to do when the typed phone is already on a card; see
 * `CUSTOMER_MATCH_MODES`.
 *
 * Optional rather than defaulted, so that absent stays absent in the type as
 * well as in the payload: everything that builds an inquiry or a quote in
 * TypeScript — the seed, the telephony path, the mini-site — has no screen to
 * ask and no answer to give, and `resolveCustomer` reads a missing one as
 * `auto`. A `.default()` here would instead make every one of those callers
 * name a mode they have no opinion about.
 */
export const customerModeSchema = z.enum(CUSTOMER_MATCH_MODES).optional();

export const createInquirySchema = z.object({
  /** Either attach to an existing customer or create one inline. */
  customerId: z.string().uuid('validation.id.invalid').optional(),
  customer: customerSchema.optional(),
  customerMode: customerModeSchema,
  source: z.enum(INQUIRY_SOURCES).default('manual'),
  sourceRef: trimmed(120).optional(),
  subject: trimmed(160).optional(),
  message: trimmed(4000).optional(),
});

export const updateInquirySchema = z.object({
  subject: trimmed(160).optional(),
  message: trimmed(4000).optional(),
  status: z.enum(INQUIRY_STATUSES).optional(),
  /**
   * Where the enquiry came in from. Editable, because it is written by whoever
   * took the call and is wrong as often as any other typed field — a lead
   * logged by hand and later found to have come off the site was stuck saying
   * "manual" forever, and every report counting sources counted it wrong.
   */
  source: z.enum(INQUIRY_SOURCES).optional(),
  /**
   * The meeting time, or `null` to say there is no longer one. Optional and
   * nullable mean different things here: absent leaves the booking alone, null
   * removes it — which is what lets one dialog both keep and clear a time.
   */
  meetingAt: z.string().datetime({ offset: true }).nullable().optional(),
});

export const noteSchema = z.object({
  body: trimmed(4000).min(1, 'validation.note.required'),
  inquiryId: z.string().uuid('validation.id.invalid').optional(),
});

export const quoteItemSchema = z.object({
  description: trimmed(300).min(1, 'validation.item.description'),
  quantity: z.coerce.number().positive('validation.item.quantity').max(1_000_000, 'validation.tooLarge'),
  unitPrice: z.coerce.number().min(0, 'validation.item.unitPrice').max(100_000_000, 'validation.tooLarge'),
});

export const quoteWriteSchema = z
  .object({
    expectedRevision: z.number().int().positive().optional(),
    galleryIds: z.array(z.string().uuid('validation.id.invalid')).max(100).optional(),
    customerId: z.string().uuid('validation.id.invalid').optional(),
    customer: customerSchema.optional(),
    customerMode: customerModeSchema,
    inquiryId: z.string().uuid('validation.id.invalid').nullish(),
    // This is the complete service description, including dictated scope.
    title: trimmed(10_000).min(1, 'validation.quoteTitle.required'),
    currency: z.enum(CURRENCIES).optional(),
    discountType: z.enum(DISCOUNT_TYPES).default('none'),
    discountValue: z.coerce.number().min(0, 'validation.discount.invalid').max(100_000_000).default(0),
    discountLabel: trimmed(80).optional(),
    notes: trimmed(4000).optional(),
    legalText: trimmed(20_000).optional(),
    /**
     * Whether the prices typed on this quote already include VAT — the pill in
     * the editor's footer. Per quote, because it describes how *this* job was
     * priced. Optional: an older client that omits it gets the business default.
     */
    pricesIncludeVat: z.boolean().optional(),
    /**
     * The rate this quote is taxed at.
     *
     * Worki holds the national number centrally and that stays the default, so
     * a rate change is one edit rather than a settings field every owner has to
     * find and keep right. But the default is not always the answer: an exempt
     * dealer charges nothing, and work that is zero-rated is still work that
     * gets quoted. So the editor may name a rate for this one quote, and the
     * number it names is frozen onto the row the same way the default is — a
     * document a customer approved has to keep showing the tax it was approved
     * with. Omitted means the platform's rate, which is what every quote
     * written before this field existed says.
     */
    vatRate: z.coerce.number().min(0, 'validation.vatRate.invalid').max(100, 'validation.vatRate.invalid').optional(),
    /**
     * QUO-012 — how long this quote may go unanswered before Worki writes the
     * follow-up. Omitted or null means the business's own threshold, which is
     * what every quote written before this field existed says.
     */
    followUpHours: z.coerce.number().int().min(1).max(720).nullable().optional(),
    /**
     * QUO-007 — how many days the customer's link stays open, chosen on this
     * quote. Omitted or null means "whatever the platform's default is", which
     * is what every quote written before this field existed says.
     */
    validDays: z
      .union([z.literal(1), z.literal(7), z.literal(14), z.literal(30)], {
        errorMap: () => ({ message: 'validation.quoteValidDays.invalid' }),
      })
      .nullish(),
    /** QUO-001 — unlimited line items (capped at a sane 500 for payload safety). */
    items: z.array(quoteItemSchema).max(500, 'validation.tooManyItems'),
  })
  /**
   * A quote needs a customer name. The editor types one fresh every time —
   * `customerId` only still arrives from older API clients — so the error is
   * reported on the name field, which is the field actually on screen.
   */
  .refine((value) => Boolean(value.customerId) || Boolean(value.customer?.name), {
    message: 'validation.customer.required',
    path: ['customer', 'name'],
  });

/**
 * 1.17 — one person, reached on every channel ticked for them.
 *
 * A send named one channel and one address, so a customer with a phone and an
 * email was sent to in two goes, through the dialog twice. The dialog now asks
 * once who it is for and posts every way of reaching them: `channels`, each with
 * the address it goes to. Each goes out as a send of its own, so the history
 * says what went where, and a channel the provider refused is not written down
 * as one that went.
 *
 * The old body — one `channel` and an optional `to` — is accepted unchanged: an
 * app installed before this posts it until it is updated. Where `channels` is
 * there it is the whole answer, and `channel` and `to` are not read.
 *
 * Every entry names its address. In the old body a blank `to` means "the one on
 * the customer card", a guess made at the server; the dialog shows that address,
 * so what is posted is what the sender saw, left as it was or changed.
 */
const sendTargetSchema = z
  .object({
    channel: z.enum(['email', 'whatsapp']),
    to: trimmed(160).default(''),
  })
  .superRefine((value, context) => {
    const refuse = (message: string) => context.addIssue({ code: z.ZodIssueCode.custom, message, path: ['to'] });
    if (!value.to) refuse(value.channel === 'email' ? 'validation.email.required' : 'validation.phone.invalid');
    else if (value.channel === 'email' && !z.string().email().safeParse(value.to).success) {
      refuse('validation.email.invalid');
    }
  });

const sendRecipientFields = {
  /**
   * Who the send is addressed to, when that is not the name on the card. A
   * quote's letter opens with it; a signed document's has no name in it, so
   * there it is accepted and left unread.
   */
  recipientName: trimmed(160).optional(),
  channels: z.array(sendTargetSchema).min(1, 'history.sendPickChannel').max(2, 'errors.validation').optional(),
};

/**
 * The sends a body asks for, whichever shape it came in: each channel with the
 * address it goes to, where the body gave one. The old body is one of them.
 *
 * Email goes first, whatever order the boxes were ticked in. It is the channel a
 * mail server can refuse, while WhatsApp on the `link` driver only hands back a
 * link for the phone to open. A refusal stops the send, so it has to come before
 * a WhatsApp send is recorded that the phone, handed an error, never opened.
 */
export function sendTargets(input: {
  channel?: 'email' | 'whatsapp' | undefined;
  to?: string | undefined;
  channels?: ReadonlyArray<{ channel: 'email' | 'whatsapp'; to: string }> | undefined;
}): Array<{ channel: 'email' | 'whatsapp'; to?: string }> {
  if (input.channels) {
    const first = (channel: 'email' | 'whatsapp') => (channel === 'email' ? 0 : 1);
    return input.channels
      .map(({ channel, to }) => ({ channel, to }))
      .sort((a, b) => first(a.channel) - first(b.channel));
  }
  return input.channel ? [{ channel: input.channel, ...(input.to ? { to: input.to } : {}) }] : [];
}

export const deliveryFormatSchema = z.enum(['link', 'pdf', 'both']);

export const sendQuoteSchema = z
  .object({
    // Optional only because `channels` may stand in for it; one of the two is asked for below.
    channel: z.enum(['email', 'whatsapp']).optional(),
    /**
     * 2.4 — the app sends the link only. `pdf` is still accepted because apps
     * installed before 2.4 post it, and it is refused until the quote is
     * approved; whether a mail carries the file is decided by the quote's
     * status, never by this field.
     */
    mode: deliveryFormatSchema.optional(),
    format: deliveryFormatSchema.optional(),
    to: trimmed(160).optional(),
    message: trimmed(2000).optional(),
    ...sendRecipientFields,
  })
  .refine((value) => value.channel || value.channels, { message: 'history.sendPickChannel', path: ['channels'] })
  /**
   * The address is only checked for shape when it is actually an address —
   * blank means "use the one on the customer", and the WhatsApp channel puts a
   * phone number in the same field. Without this the dialog handed anything at
   * all to the mailer, which failed deep in SMTP and surfaced as an unknown
   * error with no hint that the typo was in the recipient.
   */
  .refine(
    (value) => value.channels || value.channel !== 'email' || !value.to || z.string().email().safeParse(value.to).success,
    { message: 'validation.email.invalid', path: ['to'] },
  );

export const legalTemplateSchema = z.object({
  name: trimmed(120).min(1, 'validation.templateName.required'),
  body: trimmed(20_000).min(1, 'validation.templateBody.required'),
  kind: z.enum(TEMPLATE_KINDS).default('service'),
});

/* --- The back office (ADM) ------------------------------------------------ */

/** ADM-003 — a door opened or closed. The reason is audited, not the button. */
export const businessAccessSchema = z.object({
  access: z.enum(BUSINESS_ACCESS),
});

/**
 * ADM-002 — the two things Worki decides about a person who can sign in:
 * whether they may sign in at all, and whether they are Worki's own staff.
 * There is no role inside a business any more, because there is no second seat
 * for a role to distinguish.
 */
export const platformUserPatchSchema = z.object({
  active: z.boolean(),
  isPlatformAdmin: z.boolean(),
});

export const platformLeadPatchSchema = z.object({
  status: z.enum(PLATFORM_LEAD_STATUSES),
  note: trimmed(2_000).nullable().optional(),
});

export const tradeWriteSchema = z.object({
  name: trimmed(60).min(1, 'validation.tradeName.required'),
  position: z.coerce.number().int().min(0).max(999).default(0),
  active: z.boolean().default(true),
});

/**
 * ADM-007 — a plan's price and caps.
 *
 * `null` is unlimited and has to survive the round trip, so every cap is
 * nullable rather than "0 means unlimited": a plan whose gallery cap reads 0 is
 * a plan that sells nothing, and the two must not be the same value.
 */
const nullableCap = z.number().int().min(0).max(100_000).nullable();

export const platformPlanSchema = z.object({
  monthlyPriceAgorot: z.number().int().min(0).max(10_000_000),
  limits: z.object({
    connectedMailAccounts: nullableCap,
    galleries: nullableCap,
    quotesPerMonth: nullableCap,
    voiceActionsPerMonth: nullableCap,
  }),
});

export const platformSettingsSchema = z.object({
  vatRate: z.number().min(0).max(100),
  requireApproval: z.boolean(),
  trialDays: z.number().int().min(0).max(365),
  supportEmail: z.string().trim().email('validation.email.invalid').nullable(),
  /* At least a day, because an alert that fires as the subscription ends is
     not a warning; at most sixty, because past that every account is always
     "ending" and the list stops meaning anything. */
  subscriptionAlertDays: z.number().int().min(1).max(60),
});

export const reminderWriteSchema = z.object({
  notifyAt: z.string().datetime({ offset: true, message: 'validation.date.invalid' }).nullable().optional(),
  notificationChannels: z.array(z.enum(['email', 'whatsapp'])).max(2).optional(),
  notificationPhone: trimmed(30).optional(),
  title: trimmed(160).min(1, 'validation.reminderTitle.required'),
  notes: trimmed(2000).optional(),
  dueAt: z.string().datetime({ offset: true, message: 'validation.date.invalid' }),
  source: z.enum(REMINDER_SOURCES).default('manual'),
  sourceId: z.string().uuid('validation.id.invalid').nullish(),
});

export const reminderPatchSchema = z.object({
  notifyAt: z.string().datetime({ offset: true, message: 'validation.date.invalid' }).nullable().optional(),
  notificationChannels: z.array(z.enum(['email', 'whatsapp'])).max(2).optional(),
  notificationPhone: trimmed(30).optional(),
  title: trimmed(160).min(1, 'validation.reminderTitle.required').optional(),
  notes: trimmed(2000).optional(),
  dueAt: z.string().datetime({ offset: true, message: 'validation.date.invalid' }).optional(),
  status: z.enum(REMINDER_STATUSES).optional(),
});

/**
 * LEAD-009 — an AI phone agent. Only the name is required: an agent is set up
 * over several sittings, and refusing to save one without a script would mean
 * keeping the half-filled form open until the business had written it.
 *
 * `virtualNumber` is checked against the numbers the business already holds,
 * server-side — a free-text number here would silently answer nothing.
 */
export const aiAgentWriteSchema = z.object({
  name: trimmed(80).min(1, 'validation.agentName.required'),
  voice: z.enum(AGENT_VOICES).default('female'),
  virtualNumber: trimmed(40).nullish(),
  forwardTo: trimmed(40).nullish(),
  trade: trimmed(120).nullish(),
  specialties: trimmed(240).nullish(),
  inboundQuestions: trimmed(2000).nullish(),
  outboundQuestions: trimmed(2000).nullish(),
  active: z.boolean().default(true),
});

/** MKT-001 — the words of one section, in the language they were written in. */
export const landingCopySchema = z.object({
  eyebrow: trimmed(160).nullish(),
  heading: trimmed(200).nullish(),
  body: trimmed(4000).nullish(),
  bullets: z.array(trimmed(200).min(1, 'validation.required')).max(12).default([]),
  ctaLabel: trimmed(60).nullish(),
});

/**
 * MKT-001 — one section of the marketing page, as the editor submits it.
 *
 * `copy` carries only what an editor typed over the shipped text, keyed by the
 * language it was typed in. A section that has never been edited submits none,
 * and keeps reading from the locale files.
 */
export const landingSectionSchema = z.object({
  kind: z.enum(LANDING_SECTION_KINDS),
  contentKey: trimmed(120).nullish(),
  copy: z
    .record(z.string(), landingCopySchema)
    .refine((map) => Object.keys(map).every(isLocale), 'validation.landing.locale')
    .default({}),
  /*
   * A path inside this app, not a URL. The button is rendered on our own page
   * and an arbitrary destination there is an open redirect with a marketing
   * banner around it — someone with back-office access could point "start now"
   * at anything and it would still look like us.
   */
  ctaHref: z
    .string()
    .trim()
    .regex(/^\/[\w\-/?=&#.]*$/, 'validation.landing.ctaHref')
    .max(200)
    .nullish(),
  hidden: z.boolean().default(false),
});

/**
 * SIG-001 — who a document is for and what it is called.
 *
 * Everything about the file is elsewhere: the compose route extends this with
 * the places to sign and the channel, and the edit dialog sends it on its own.
 * What is signed is an uploaded file, so nothing here describes the document's
 * contents any more.
 */
export const signatureDocumentWriteSchema = z.object({
  additionalSigners:z.array(z.lazy(()=>additionalSignerSchema)).max(LIMITS.maxSigners-1).optional(),
  expectedRevision: z.number().int().positive().optional(),
  digitalDocument: digitalDocumentSchema.nullish(),
  body: trimmed(50_000).optional(),
  title: trimmed(160).min(1, 'validation.signature.title'),
  /*
   * No `body`. A document to sign is a file now, and the typed-out text this
   * used to carry was the old two-modal screen's other half. Rows written that
   * way still render and still send — the column and everything that reads it
   * stay — but nothing writes one any more, and leaving the field here meant an
   * edit that changed only a title arrived with `body: ''` and wiped one.
   */
  /*
   * Who is being asked to sign, typed here rather than picked from a list of
   * customers. A document goes to whoever is standing in front of the sender,
   * and requiring a customer record first turns a two-minute form into data
   * entry. The name is required because a signed document has to say whose
   * signature it was expecting; an address and a number are how it reaches
   * them, and either one on its own is enough to send.
   */
  recipientNames: z.array(trimmed(120).min(2)).max(20).optional(),
  recipientPhones: z.array(trimmed(40).min(9)).max(20).optional(),
  recipientEmails: z.array(z.string().trim().email().max(160)).max(20).optional(),
  recipientName: trimmed(120).min(2, 'validation.signature.recipientName'),
  recipientPhone: trimmed(40).min(9, 'validation.phone.invalid').nullish(),
  recipientEmail: z.string().trim().email('validation.email.invalid').max(160).nullish(),
});

/**
 * SIG-003 — somebody else who has to sign the same document.
 *
 * Named exactly like the first: the recipient above is the first signer, and
 * these are the ones after them. Kept as a separate list rather than folding
 * the first into it, so that every screen, mail and stored row that has only
 * ever known one recipient keeps working unchanged.
 */
export const additionalSignerSchema = z.object({
  names: z.array(trimmed(120).min(2)).max(20).optional(),
  phones: z.array(trimmed(40).min(9)).max(20).optional(),
  emails: z.array(z.string().trim().email().max(160)).max(20).optional(),
  name: trimmed(120).min(2, 'validation.signature.recipientName'),
  phone: trimmed(40).min(9, 'validation.phone.invalid').nullish(),
  email: z.string().trim().email('validation.email.invalid').max(160).nullish(),
});

/**
 * SIG-003 — one place on the document where the signature is stamped.
 *
 * A fraction of the page in both axes, so the box means the same thing on the
 * phone it was placed from and on the A4 page it is drawn onto. The upper
 * bound is on the far edge rather than the origin: a box may start at 0.9 and
 * must not then run off the paper.
 */
const fraction = (message: string) => z.number().min(0, message).max(1, message);

export const signatureAnchorSchema = z
  .object({
    annotation: z.boolean().optional(),
    page: z.number().int().min(1, 'validation.signature.anchor'),
    /* Optional, and absent means a drawn signature: every box placed before
       free text existed is one, and a stored row must keep meaning what it
       meant when it was written. */
    kind: z.enum(['signature', 'text']).optional(),
    /* Which signer's box this is, 1-based. Optional and absent means the first,
       for the same reason `kind` is: a stored row must keep meaning what it
       meant when it was written. */
    signer: z.number().int().min(1, 'validation.signature.anchor').max(LIMITS.maxSigners, 'validation.signature.anchor').optional(),
    /* 1.1 — who fills a box in, and what the sender put there if it is them.
       Optional for the reason the two above are. The words are kept as typed,
       spaces and all: a placement screen saving as the sender types would
       otherwise get back a line with its last space taken off, mid-word. The
       stamp and the send trim them where it matters. */
    filledBy: z.enum(['signer', 'sender']).optional(),
    text: z.string().max(200, 'validation.tooLong').optional(),
    /* 1.1 — the sender's own signature, on a signature box the sender fills in.
       The shape and the size only; whether the bytes are a PNG anything can draw
       is settled on the server, where they are decoded. */
    image: z
      .string()
      .max(
        DOCUMENT_SIGNATURE_PREFIX.length + Math.ceil(LIMITS.maxSenderSignatureBytes / 3) * 4,
        'validation.signature.tooLarge',
      )
      .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/, 'validation.signature.image')
      .optional(),
    x: fraction('validation.signature.anchor'),
    y: fraction('validation.signature.anchor'),
    width: z.number().min(0.02, 'validation.signature.anchor').max(1, 'validation.signature.anchor'),
    height: z.number().min(0.01, 'validation.signature.anchor').max(1, 'validation.signature.anchor'),
  })
  .refine((box) => box.x + box.width <= 1.0001 && box.y + box.height <= 1.0001, 'validation.signature.anchor')
  .transform(tidyAnchor);

/**
 * 1.1 — a box stored with only the fields that mean something on it.
 *
 * A box the sender fills in belongs to no signer, so a signer number posted
 * with one — a box copied from a second signer's, then handed to the sender —
 * is dropped rather than left to make somebody its owner. What the sender put
 * in a box stays only on the kind of box that shows it: words on a text box, a
 * drawing on a signature box. Who fills in, the words and the drawing are
 * dropped from every signer's box, where nothing reads them and the signing
 * page would still be handed them.
 */
function tidyAnchor(anchor: SignatureAnchor): SignatureAnchor {
  const { filledBy, text, image, signer, ...box } = anchor;
  if (filledBy === 'sender') {
    return box.kind === 'text'
      ? { ...box, filledBy, ...(text === undefined ? {} : { text }) }
      : { ...box, filledBy, ...(image === undefined ? {} : { image }) };
  }
  return { ...box, ...(signer === undefined ? {} : { signer }) };
}

export const signatureAnchorsWriteSchema = z.object({
  expectedRevision: z.number().int().positive().optional(),
  anchors: z.array(signatureAnchorSchema).max(LIMITS.maxSignatureAnchors, 'validation.signature.anchorCount'),
});

/** 1.1 — a box the sender fills in before sending, rather than a signer after. */
export function isSenderBox(anchor: SignatureAnchor): boolean {
  return anchor.filledBy === 'sender';
}

/** 1.1 — a text box the sender types into before sending. */
export function isSenderText(anchor: SignatureAnchor): boolean {
  return anchor.kind === 'text' && isSenderBox(anchor);
}

/** 1.1 — a signature box the sender signs before sending: "my signature". */
export function isSenderSignature(anchor: SignatureAnchor): boolean {
  return anchor.kind !== 'text' && isSenderBox(anchor);
}

/**
 * 1.1 — the signer who answers a box, by position: the one who draws in it or
 * types into it. Null for a box the sender fills in, which belongs to none of
 * them. Absent is the first signer, as everywhere a box is read.
 */
export function anchorSigner(anchor: SignatureAnchor): number | null {
  return isSenderBox(anchor) ? null : (anchor.signer ?? 1);
}

/**
 * 1.1 — the boxes the sender marked to fill in and left empty, by index: a text
 * box with no words in it, or a signature box with no drawing.
 *
 * A send is refused while there are any. Printing nothing would send the
 * signer a document with a blank the business meant to fill — a price or a
 * date left out by mistake, or the business's own signature — and a sent
 * document cannot be edited, so the fix would be a withdrawal and a new
 * document with a new link.
 */
export function emptySenderBoxes(anchors: SignatureAnchor[]): number[] {
  return anchors.flatMap((anchor, index) =>
    (isSenderText(anchor) && !anchor.text?.trim()) || (isSenderSignature(anchor) && !anchor.image) ? [index] : [],
  );
}

/**
 * 1.1 — the refusal for a send that still has an empty box of the sender's, as
 * an i18n key, or null when there is none. Named by the first such box, so the
 * sender is told what to do with it: type into it, or sign it.
 */
export function emptySenderBoxError(
  anchors: SignatureAnchor[],
): 'signing.senderTextMissing' | 'signing.senderSignatureMissing' | null {
  const first = emptySenderBoxes(anchors)[0];
  if (first === undefined) return null;
  return isSenderText(anchors[first]!) ? 'signing.senderTextMissing' : 'signing.senderSignatureMissing';
}

/**
 * 1.1 — the size a line of typing is set at inside a box, in the box's own
 * units: points on the stamped PDF, pixels on screen.
 *
 * As large as the box's height allows, then shrunk until the whole line fits
 * across, so nothing typed is ever cut off. `widthAtOne` is the line's width at
 * a size of one, which each side measures in its own font; a line's width grows
 * in step with its size, so one measurement is enough.
 *
 * One rule for the stamp and the signing page, so the answer a signer sees in
 * the box is the answer that is printed there.
 */
export function typedTextSize(box: { width: number; height: number }, widthAtOne: number): number {
  const byHeight = box.height * 0.7;
  return widthAtOne > 0 ? Math.min(byHeight, box.width / widthAtOne) : byHeight;
}

/**
 * How the link reaches whoever has to sign it.
 *
 * The same two channels the quote goes out on, and for the same reason: a
 * document to sign is handed over the way the business already talks to that
 * customer, which in this market is more often WhatsApp than mail.
 *
 * `to` is blank by default and means "whoever the document says" — the address
 * on an email send, the number on a WhatsApp one. It is checked for the shape
 * of an address only when it is one; a phone number in the same field is not a
 * malformed address.
 */
export const sendSignatureSchema = z
  .object({
    expectedRevision: z.number().int().positive().optional(),
    retryTargets: z.array(z.object({signer:z.number().int().min(1).max(LIMITS.maxSigners),channel:z.enum(['email','whatsapp'])})).min(1).max(LIMITS.maxSigners*2).optional(),
    format: deliveryFormatSchema.optional(),
    channel: z.enum(['email', 'whatsapp']).default('email'),
    to: trimmed(160).optional(),
    // 1.17 — the same list of channels a quote's send takes, for one recipient.
    ...sendRecipientFields,
  })
  .refine(
    (value) => value.channels || value.channel !== 'email' || !value.to || z.string().email().safeParse(value.to).success,
    { message: 'validation.email.invalid', path: ['to'] },
  );

/**
 * SIG-002 — what the customer sends back.
 *
 * The drawing arrives as a PNG data URL because that is what a canvas gives,
 * and it is bounded here rather than at the route: a signature is a few strokes
 * and anything the size of a photograph is not one.
 */
/**
 * SIG-004 — the recipient refuses, with or without saying why.
 *
 * Free text and optional. Whatever is wrong with a document is a sentence about
 * that document, not one of four buttons, and a refusal that will not go
 * through without an explanation is a refusal that is never sent.
 */
export const declineDocumentSchema = z.object({
  expectedRevision: z.number().int().positive().optional(),
  reason: trimmed(500).nullish(),
});

export const signDocumentSchema = z.object({
  expectedRevision: z.number().int().positive().optional(),
  fieldValues: documentValuesSchema.default({}),
  /**
   * SIG-003 — the boxes on an uploaded page that asked to be typed in, keyed by
   * the anchor's own index. Index rather than an id because a placement is
   * frozen when the link goes out: the list the signer answered is the list the
   * document was sent with, and nothing can renumber it in between.
   */
  anchorTexts: z
    .record(z.string().regex(/^\d+$/, 'validation.signature.anchor'), trimmed(200))
    .refine((values) => Object.keys(values).length <= LIMITS.maxSignatureAnchors, 'validation.signature.anchorCount')
    .default({}),
  signerName: trimmed(120).min(2, 'validation.signature.signerName'),
  signature: z
    .string()
    .startsWith('data:image/png;base64,', 'validation.signature.image')
    .max(LIMITS.maxSignatureBytes * 2, 'validation.signature.tooLarge'),
});

/** QUO-014 — customer submits the 4-digit code shown on screen. */
export const approveQuoteSchema = z.object({
  expectedRevision: z.number().int().positive().optional(),
  code: z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'validation.approvalCode.invalid'),
});

export const startQuoteApprovalSchema = z.object({
  expectedRevision: z.number().int().positive().optional(),
});

export const rejectQuoteSchema = z.object({
  reason: z.enum(REJECTION_REASONS, { errorMap: () => ({ message: 'validation.rejectionReason.required' }) }),
  note: trimmed(1000).optional(),
});

/** Shared list query contract (INFRA-007 Global UI Standard). */
export const listQuerySchema = z.object({
  q: z.string().trim().max(160).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  sort: z.string().max(40).optional(),
  dir: z.enum(['asc', 'desc']).default('desc'),
});

export const quoteListQuerySchema = listQuerySchema.extend({
  month: z.coerce.number().int().min(1).max(12).optional(),
  year: z.coerce.number().int().min(1970).max(2200).optional(),
  tab: z.enum(QUOTE_TABS).default('all'),
  range: z.enum(TIME_RANGES).default('all'),
  customerId: z.string().uuid().optional(),
});

/** LIB — renaming a stored document. The file itself is never re-titled. */
export const libraryWriteSchema = z.object({
  title: z.string().trim().min(1).max(160),
});

/** 1.45 — the library's list, narrowed to one folder when a folder is asked for. */
export const libraryListQuerySchema = listQuerySchema.extend({
  folderId: z.string().uuid().optional(),
});

/** 1.45 — a folder is a name and nothing else; what it holds is the filing. */
export const libraryFolderSchema = z.object({
  name: trimmed(60).min(1, 'validation.folderName.required'),
});

/**
 * 1.45 — file a document in a folder, or `null` to take it out of every folder.
 *
 * `null` rather than an absent field, as the invoice archive's move has it:
 * "leave the filing alone" is not something this endpoint does.
 */
export const libraryMoveSchema = z.object({
  folderId: z.string().uuid().nullable(),
});

/** Pages are taken out, never moved, so the ones kept are named in the order they already have. */
const ascending = (pages: number[]): boolean => pages.every((page, index) => index === 0 || page > pages[index - 1]!);

/**
 * 1.44 — a stored document edited: which of its own pages stay, and the version
 * the screen had open. The files that are added travel beside these in the same
 * multipart form, which is why `keep` is JSON text.
 *
 * `updatedAt` is there because the pages are named by number: a file another
 * device has since changed would otherwise lose the wrong ones without a word.
 */
export const libraryPagesSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  updatedAt: z.string({ required_error: 'validation.library.pages', invalid_type_error: 'validation.library.pages' }).min(1).max(40),
  keep: z.preprocess(
    (raw) => {
      try {
        return typeof raw === 'string' ? (JSON.parse(raw) as unknown) : raw;
      } catch {
        return undefined;
      }
    },
    z
      .array(z.number().int('validation.library.pages').min(1, 'validation.library.pages'), {
        required_error: 'validation.library.pages',
        invalid_type_error: 'validation.library.pages',
      })
      .max(LIMITS.maxScanPages, 'validation.library.pages')
      .refine(ascending, 'validation.library.pages'),
  ),
});

/**
 * The merged history. `tab` is a quote status, so asking for one narrows the
 * list to quotes on its own — a signing document is not "approved".
 */
export const historyListQuerySchema = listQuerySchema.extend({
  month: z.coerce.number().int().min(1).max(12).optional(),
  year: z.coerce.number().int().min(1970).max(2200).optional(),
  kind: z.enum(HISTORY_KINDS).default('all'),
  tab: z.enum(QUOTE_TABS).default('all'),
  range: z.enum(TIME_RANGES).default('all'),
});

export const inquiryListQuerySchema = listQuerySchema.extend({
  status: z.enum(INQUIRY_STATUSES).optional(),
  source: z.enum(INQUIRY_SOURCES).optional(),
  range: z.enum(TIME_RANGES).default('all'),
  /**
   * Enquiries that have an appointment on them. Kept apart from
   * `status=meeting_scheduled` because `range` filters on when the enquiry
   * arrived, and a diary wants the ones whose meeting is still ahead — a lead
   * from March with a meeting next week belongs in it.
   */
  hasMeeting: z
    .enum(['true', 'false'])
    .optional()
    // Not `z.coerce.boolean()`: that reads the string "false" as true, and
    // `buildQuery` puts a literal `false` on the wire rather than dropping it.
    .transform((value) => (value === undefined ? undefined : value === 'true')),
  meetingFrom: z.string().datetime({ offset: true }).optional(),
  meetingTo: z.string().datetime({ offset: true }).optional(),
});

export const reminderListQuerySchema = listQuerySchema.extend({
  status: z.enum(REMINDER_STATUSES).optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
});

export const snoozeReminderSchema = z.object({
  until: z.string().datetime({ offset: true, message: 'validation.date.invalid' }),
});

export const callListQuerySchema = listQuerySchema.extend({
  month: z.coerce.number().int().min(1).max(12).optional(),
  year: z.coerce.number().int().min(1900).max(2200).optional(),
  status: z.enum(CALL_STATUSES).optional(),
  direction: z.enum(['inbound', 'outbound']).optional(),
  range: z.enum(TIME_RANGES).default('all'),
});

export const updateCallSchema = z.object({
  notes: trimmed(4000).optional(),
  status: z.enum(CALL_STATUSES).optional(),
});

export const clickToCallSchema = z.object({
  phone: trimmed(40).min(5, 'validation.phone.invalid'),
  customerId: z.string().uuid('validation.id.invalid').optional(),
});

export const miniSiteSettingsSchema = z.object({
  // Every message has to be an i18n key: an empty or 2-character slug used to
  // reach a Hebrew UI as zod's own English "String must contain at least 3
  // character(s)".
  slug: z.string().trim().min(3, 'validation.slug.tooShort').max(60, 'validation.tooLong').regex(/^[a-z0-9-]+$/, 'validation.slug.invalid'),
  about: trimmed(5000).optional(),
  /*
   * The trades the business works in, as chips on the mini-site's "about" panel.
   * A list and not free text because a visitor scans them, and because the same
   * list is what a future search index would filter on. Twelve is a display
   * limit: past that the chip row wraps into a wall.
   */
  fields: z.array(trimmed(40).min(1, 'validation.required')).max(12).optional(),
  /*
   * Nitzan's gallery page. Null clears and absent keeps: the slug form saves
   * through here without any of them, and must not be read as "clear them".
   * The year opened rather than years active, so the figure never goes stale.
   */
  slogan: trimmed(120).nullish(),
  areas: z.array(z.enum(SERVICE_AREAS)).max(SERVICE_AREAS.length).optional(),
  foundedYear: z.coerce
    .number()
    .int('validation.foundedYear.invalid')
    .min(1900, 'validation.foundedYear.invalid')
    .refine((year) => year <= new Date().getFullYear(), 'validation.foundedYear.invalid')
    .nullish(),
  projectsCount: z.coerce.number().int().min(0).max(100_000).nullish(),
});

/** A visitor's heart says which way it goes, so a retry or a second tab cannot flip it back. */
export const galleryLikeSchema = z.object({ liked: z.boolean() });

export const gallerySchema = z.object({
  name: trimmed(120).min(1, 'validation.galleryName.required'),
  description: trimmed(3000).optional(),
  price: z.coerce.number().min(0).max(100_000_000).nullish(),
  priceFrom: z.boolean().default(false),
  availableQuantity: z.coerce.number().int().min(0).max(1_000_000).nullish(),
  published: z.boolean().default(true),
  /*
   * Whether the album is being promoted. Kept on the album rather than copied
   * into an offer row: the promoted thing *is* this body of work, with its
   * images and its price, and a copy would drift from it the first time either
   * one was edited.
   */
  onSale: z.boolean().default(false),
  /** The town the work was done in, shown with a pin on the album card. */
  city: trimmed(80).optional(),
});

/* SITE-7.1 — the albums in the order they now sit, sent whole. Capped at the
   most albums a site may hold, so a long list is a bad request rather than a
   long write. */
export const galleryOrderSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'validation.noSelection').max(LIMITS.maxGalleries),
});

export const offerSchema = z.object({
  name: trimmed(120).min(1, 'validation.offerName.required'),
  description: trimmed(3000).optional(),
  price: z.coerce.number().min(0).max(100_000_000).nullish(),
  priceFrom: z.boolean().default(false),
  /*
   * How long the offer runs, in days, and not the date it ends: "a week" is the
   * decision the business is actually making, and a date picker asks them to do
   * the arithmetic and to re-do it every time they repost. The server turns it
   * into the absolute `expires_at` the page reads — a duration stored as-is
   * would mean an offer that never ends.
   */
  validDays: z.coerce.number().int('validation.validDays.invalid').min(1, 'validation.validDays.invalid').max(365, 'validation.validDays.invalid').default(7),
});

export const reviewSchema = z.object({
  customerName: trimmed(120).min(1, 'validation.customerName.required'),
  phone: trimmed(40).optional(),
  showPhone: z.boolean().default(false),
  city: trimmed(80).optional(),
  body: trimmed(3000).min(1, 'validation.note.required'),
  /*
   * One to five whole stars. Optional rather than defaulted: a review typed in
   * without a score is a review with no score, and defaulting it to anything
   * would put a mark in the average on the customer's behalf. Zero is rejected
   * for the same reason — "no stars" is `undefined`, not a nil verdict.
   */
  rating: z.number().int('validation.rating.invalid').min(1, 'validation.rating.invalid').max(5, 'validation.rating.invalid').optional(),
  /*
   * The same verdict broken into the three things a customer of a trade
   * business actually judges: the service, whether the work landed on time, and
   * what it was worth. Each is scored on the same one-to-five scale as `rating`
   * — one scale for the whole product, so the average, the stars on the public
   * page and the bars in the breakdown all mean the same thing.
   *
   * All three or none: a partial breakdown would have to be averaged over
   * whichever parts were filled in, which is a different question from the one
   * the labels ask. `rating` stays for reviews collected before the breakdown
   * existed, and is what the average falls back to for those rows.
   */
  ratingService: z.number().int('validation.rating.invalid').min(1, 'validation.rating.invalid').max(5, 'validation.rating.invalid').optional(),
  ratingTimeliness: z.number().int('validation.rating.invalid').min(1, 'validation.rating.invalid').max(5, 'validation.rating.invalid').optional(),
  ratingValue: z.number().int('validation.rating.invalid').min(1, 'validation.rating.invalid').max(5, 'validation.rating.invalid').optional(),
});

export const miniSiteInquirySchema = z.object({
  galleryId: z.string().uuid('validation.id.invalid').optional(),
  name: trimmed(120).min(2, 'validation.customerName.required'),
  phone: trimmed(40).min(5, 'validation.phone.invalid'),
  email: z.union([emailSchema, z.literal('')]).optional(),
  message: trimmed(3000).optional(),
  // Communications Law s.30A wants consent given *before* the promotion and
  // provable afterwards, so it has to be its own answer rather than something
  // read into the act of sending an inquiry. It defaults to false and is never
  // required: refusing to be marketed to must not stop a visitor asking a
  // question, and the business still owes them a reply.
  marketingConsent: z.boolean().default(false),
});

export const bulkIdsSchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'validation.noSelection').max(LIMITS.maxBulkInvoices),
});

/**
 * INV-006 — send a selection of invoices on as one document.
 *
 * Email takes an address and gets the PDF attached. WhatsApp cannot carry an
 * attachment through a deep link, so it gets a link to the same document and
 * the number is optional: without one the message opens in WhatsApp with no
 * conversation chosen, which is how someone forwards it to a group.
 */
export const SHARE_CHANNELS = ['email', 'whatsapp'] as const;

/**
 * 1.9 — what the mail carries: the merged PDF itself, or a link to it.
 *
 * WhatsApp has no part in this choice. A deep link cannot carry a file, so what
 * it carries is always the link.
 */
export const SHARE_DELIVERIES = ['pdf', 'link'] as const;

/**
 * Which ways this batch is being sent, whichever shape the client asked in.
 *
 * `channels` is the list; `channel` is the single value every installed copy of
 * the app was built to send, and it keeps working because a phone updates when
 * its owner lets it and not when the server does.
 */
export function shareChannelsOf(input: { channel?: string; channels?: string[] }): Array<'email' | 'whatsapp'> {
  const asked = input.channels ?? (input.channel ? [input.channel] : []);
  return SHARE_CHANNELS.filter((channel) => asked.includes(channel));
}

/**
 * The number a WhatsApp message is aimed at, or none for the contact picker.
 *
 * Older clients had one recipient box for both channels and sent whatever was
 * in it as `to`, so for those a `to` *is* the number. A client that names its
 * channels has a box for each and says `phone`, and then `to` is an address and
 * nothing else — sending an email address to WhatsApp would open a chat with a
 * number made of digits pulled out of it.
 */
export function sharePhoneOf(input: { channel?: string; channels?: string[]; to?: string; phone?: string }):
  | string
  | undefined {
  if (input.channels) return input.phone;
  return input.phone ?? input.to;
}

export const invoiceShareSchema = bulkIdsSchema
  .extend({
    locale: z.enum(['he', 'en']).optional(),
    /** What installed apps send. Superseded by `channels`, still accepted. */
    channel: z.enum(SHARE_CHANNELS).optional(),
    /**
     * INV-006 — the accountant gets the mail and the bookkeeper gets the
     * WhatsApp, from one press. They were a single choice until the person who
     * files these pointed out that the two are not alternatives: the same batch
     * goes to both, and doing it twice means building the document twice.
     */
    channels: z.array(z.enum(SHARE_CHANNELS)).min(1, 'validation.noChannel').max(2).optional(),
    /** The email address. */
    to: trimmed(160).optional(),
    /** The WhatsApp number; blank opens on the contact picker. */
    phone: trimmed(40).optional(),
    /**
     * What the sender is calling this batch — "September 2026", "Q3 to the
     * bookkeeper". It names the attachment, the file behind the link and the
     * subject line, because `invoices-40.pdf` arriving from a business the
     * accountant has fifty of says nothing about which forty they are.
     */
    title: trimmed(80).optional(),
    /**
     * 1.9 — `pdf` attaches the merged document to the mail, `link` sends the mail
     * with the link and no file. Left out, the server goes by size, which is all
     * the copies of the app that are installed have ever asked of it: a phone
     * updates when its owner lets it and not when the server does. A `pdf` that
     * is too big to attach still goes as a link.
     */
    delivery: z.enum(SHARE_DELIVERIES).optional(),
    /**
     * 1.8 — the folders this batch is, when folders are what is being handed
     * over. The ids above are invoices and say nothing of which folder they
     * came from, and a folder that was sent has to say when. Left out by every
     * copy of the app that predates it, and then nothing is written down. No
     * more of them than invoices, as a folder with none has nothing to send.
     */
    folderIds: z.array(z.string().uuid()).max(LIMITS.maxBulkInvoices).optional(),
  })
  .refine((value) => shareChannelsOf(value).length > 0, {
    message: 'validation.noChannel',
    path: ['channels'],
  })
  .refine((value) => !shareChannelsOf(value).includes('email') || Boolean(value.to), {
    message: 'validation.email.required',
    path: ['to'],
  })
  .refine(
    (value) =>
      !shareChannelsOf(value).includes('email') || !value.to || z.string().email().safeParse(value.to).success,
    {
      message: 'validation.email.invalid',
      path: ['to'],
    },
  );

/** INV-006 — a folder is a name and nothing else; what it holds is the filing. */
export const invoiceFolderSchema = z.object({
  name: trimmed(60).min(1, 'validation.folderName.required'),
});

/**
 * Section 3 (Nitzan, 05.10) — one invoice of a quick scan, as the screen files it.
 *
 * It names its files by their index in the `files[]` sent beside it, in page
 * order. Every field the screen insists on is required here too: an invoice is
 * not filed without its amount, its other party, its date and its number.
 * `party` is the supplier of an expense and the customer of income (see
 * `InvoiceRecord.supplier`). Existing invoices are reused rather than saving an
 * invoice the archive already holds under its issuer, number, currency, date and direction.
 */
export const invoiceScanInvoiceSchema = z.object({
  files: z.array(z.number().int().min(0)).min(1),
  pdfPages: z.array(z.number().int().min(1).max(Number.MAX_SAFE_INTEGER))
    .min(1, 'validation.invoice.pdfPages').max(LIMITS.maxScanPages, 'validation.invoice.pdfPages')
    .refine(pages => pages.every((page, index) => index === 0 || page > pages[index - 1]!), 'validation.invoice.pdfPages')
    .optional(),
  direction: z.enum(INVOICE_DIRECTIONS),
  party: trimmed(120).min(1, 'validation.invoice.party'),
  number: trimmed(60).min(1, 'validation.invoice.number'),
  amount: z
    .number({ invalid_type_error: 'validation.invoice.amount' })
    .positive('validation.invoice.amount')
    .max(100_000_000, 'validation.tooLarge'),
  currency: z.enum(CURRENCIES).default('ILS'),
  issuedAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.invoice.date'),
  docType: z.enum(INVOICE_DOC_TYPES).nullable().default(null),
  allocationNumber: trimmed(20).nullable().default(null),
}).superRefine((invoice, ctx) => {
  if (invoice.pdfPages && invoice.files.length !== 1) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pdfPages'], message: 'validation.invoice.pdfPages' });
  }
});

/**
 * Section 3 — `POST /api/invoices/scan/save`. Multipart: the same `files[]` the
 * read was sent, and this as JSON text in a `manifest` field. A file may belong
 * to one invoice only. The folder is optional: an existing one by id, a new one
 * by name (an existing folder of that name is used rather than refused), or
 * null for none.
 */
export const invoiceScanSaveSchema = z.object({
  invoices: z.array(invoiceScanInvoiceSchema).min(1).max(LIMITS.maxScanPages),
  folder: z
    .union([z.object({ id: z.string().uuid() }), invoiceFolderSchema])
    .nullable()
    .default(null),
});
export type InvoiceScanInvoice = z.input<typeof invoiceScanInvoiceSchema>;
export type InvoiceScanSave = z.input<typeof invoiceScanSaveSchema>;

/**
 * Move a selection into a folder, or `null` to take it out of every folder.
 *
 * `null` rather than omitting the field: "leave the filing alone" is not one of
 * the things this endpoint does, so an absent id would be an ambiguity rather
 * than a shorthand.
 */
export const invoiceMoveSchema = bulkIdsSchema.extend({
  folderId: z.string().uuid().nullable(),
});

/**
 * 1.14 — the totals of a hand-picked set of invoices.
 *
 * Its own ceiling, not `bulkIdsSchema`'s. That one is how many documents a
 * single action may open at once — sharing merges them into a PDF in memory —
 * and this only adds rows up, which costs next to nothing however many there
 * are. A selection ticked a page at a time can outgrow the bulk ceiling, and
 * the bar should keep adding it up rather than fall back to the filter's
 * figures while that very selection is on screen.
 */
export const invoiceSummarySchema = z.object({
  ids: z.array(z.string().uuid()).min(1, 'validation.noSelection').max(10000),
});

/**
 * INV-006 — an invoice that never came through a mailbox.
 *
 * The amount is the one field that is required, and it is required because it
 * is the one that can be wrong in a way that matters. A missing supplier makes
 * a row harder to find; a missing amount makes the period total short, and the
 * period total is the number the whole archive exists to produce. Nothing here
 * reads text off a photograph, so on the invoice this form is for — paper,
 * handed over at a counter — the person filing it is the only one who knows.
 *
 * Everything arrives as multipart text, where a price and a date are both just
 * strings, so both are coerced rather than trusted.
 */
export const invoiceUploadSchema = z.object({
  supplier: trimmed(120).optional(),
  number: trimmed(60).optional(),
  amount: z.coerce
    .number({ invalid_type_error: 'validation.invoice.amount' })
    .positive('validation.invoice.amount')
    .max(100_000_000, 'validation.tooLarge'),
  currency: z.enum(CURRENCIES).default('ILS'),
  /*
   * `<input type="date">` produces `YYYY-MM-DD` and nothing else, so that is
   * what is accepted — `new Date(...)` would take a far wider set of strings
   * and quietly turn the ones it did not understand into `Invalid Date`, which
   * is stored as `null` and files the invoice under the day it was uploaded.
   */
  issuedAt: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'validation.date.invalid')
    .optional(),
});

export const bulkInquiryStatusSchema = bulkIdsSchema.extend({
  status: z.enum(INQUIRY_STATUSES),
});

export type SignupInput = z.infer<typeof signupSchema>;
export type GoogleSignupInput = z.infer<typeof googleSignupSchema>;
export type GoogleNativeInput = z.infer<typeof googleNativeSchema>;
export type VerifySignupInput = z.infer<typeof verifySignupSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type QuoteWriteInput = z.infer<typeof quoteWriteSchema>;
export type LandingSectionInput = z.infer<typeof landingSectionSchema>;
export type SignatureDocumentWriteInput = z.infer<typeof signatureDocumentWriteSchema>;
export type SignatureAnchorsWriteInput = z.infer<typeof signatureAnchorsWriteSchema>;
export type AdditionalSignerInput = z.infer<typeof additionalSignerSchema>;
export type SignDocumentInput = z.infer<typeof signDocumentSchema>;
export type DeclineDocumentInput = z.infer<typeof declineDocumentSchema>;
export type SendSignatureInput = z.infer<typeof sendSignatureSchema>;
export type QuoteItemInput = z.infer<typeof quoteItemSchema>;
export type CreateInquiryInput = z.infer<typeof createInquirySchema>;
export type CustomerMatchMode = (typeof CUSTOMER_MATCH_MODES)[number];
export type ReminderWriteInput = z.infer<typeof reminderWriteSchema>;
export type AiAgentWriteInput = z.infer<typeof aiAgentWriteSchema>;
export type SendQuoteInput = z.infer<typeof sendQuoteSchema>;
export type InvoiceShareInput = z.infer<typeof invoiceShareSchema>;
export type InvoiceFolderInput = z.infer<typeof invoiceFolderSchema>;
export type InvoiceUploadInput = z.infer<typeof invoiceUploadSchema>;
export type InvoiceMoveInput = z.infer<typeof invoiceMoveSchema>;
export type ListQuery = z.infer<typeof listQuerySchema>;
export type QuoteListQuery = z.infer<typeof quoteListQuerySchema>;
export type HistoryListQuery = z.infer<typeof historyListQuerySchema>;
export type LibraryWriteInput = z.infer<typeof libraryWriteSchema>;
export type InquiryListQuery = z.infer<typeof inquiryListQuerySchema>;
export type ReminderListQuery = z.infer<typeof reminderListQuerySchema>;
export type CallListQuery = z.infer<typeof callListQuerySchema>;
export type GalleryInput = z.infer<typeof gallerySchema>;
export type OfferInput = z.infer<typeof offerSchema>;
